import React, { useState } from "react";
import { Alert, Button } from "antd";

import { useOfficeFeatures } from "../officeFeatures";
import { openPlanModal } from "./planModal";

/**
 * "N of 25 free requests used" / "Free requests used up — choose a plan" /
 * "Your office needs a plan".
 *
 * Running out is otherwise only discovered by clicking Request and being
 * refused, which reads as a fault rather than a plan boundary. Reads the office's
 * plan gate from the app-wide office status (usage.services.access), so the
 * banner, the menu and the page locks always agree. Renders nothing for a paying,
 * complimentary or unmetered office.
 *
 * Super admins see what the office sees, so we know what our customers are shown.
 * The server never gates a super admin (``gated`` is false for them), so for them
 * a blocked office is read from its state instead; the button opens the plans as a
 * preview (MultiOfficeCheckout).
 */
export default function TrialBanner() {
  const status = useOfficeFeatures();
  const [hidden, setHidden] = useState(false);
  const plan = status && status.plan;
  if (!plan || hidden) return null;

  const trial = plan.trial;
  const blocked = plan.gated || (status.isSuperAdmin && plan.state === "blocked");
  if (blocked) {
    const exhausted = plan.reason === "trial_exhausted";
    return (
      <Alert
        type="warning"
        showIcon
        banner
        style={{ marginBottom: 0 }}
        message={
          exhausted
            ? (trial && trial.quota
              ? `You've used all ${trial.quota} of your free requests.`
              : "You've used all of your free requests.")
            : "Your office needs a plan to keep using Smilepass."
        }
        description={
          exhausted
            ? "Everything already fetched stays available. Choose a plan to make new requests."
            : "Choose a plan for your office to keep making requests."
        }
        action={
          <Button size="small" type="primary" onClick={() => openPlanModal({ reason: plan.reason })}>
            Choose a plan
          </Button>
        }
      />
    );
  }
  if (plan.state === "trial" && trial && trial.active) {
    return (
      <Alert
        type="info"
        showIcon
        banner
        closable
        onClose={() => setHidden(true)}
        style={{ marginBottom: 0 }}
        message={`Free trial — ${trial.used} of ${trial.quota} requests used.`}
        action={
          <Button size="small" onClick={() => openPlanModal({ reason: "trial" })}>
            See plans
          </Button>
        }
      />
    );
  }
  return null;
}
